<template>
  <div ref="rootRef" :dir="isRtl !== false ? 'rtl' : 'ltr'" class="relative w-full custom-selector-container">
    <button
      type="button"
      class="btn w-full justify-start font-normal text-left h-auto py-2 px-3 min-h-0"
      @click="toggleDropdown"
      aria-haspopup="listbox"
      :aria-expanded="isOpen !== false"
    >
      <div v-if="selectedOptionObj !== null" class="flex items-center space-x-2 w-full">
        <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'" :style="isRtl === true ? 'padding-right:2%;' : ''">
          <span class="block text-sm font-medium truncate relative w-full">{{ selectedOptionObj.label || selectedOptionObj }}</span>
        </div>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60 flex-shrink-0 ml-auto" />
      </div>
      <div v-else class="flex items-center justify-between w-full">
        <span class="text-sm text-base-content opacity-70">{{ placeholder }}</span>
        <Icon icon="heroicons:chevron-down-20-solid" class="w-5 h-5 text-base-content opacity-60" />
      </div>
    </button>

    <Teleport to="body">
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div
          v-if="isOpen === true"
          ref="dropdownListRef"
          class="flex max-h-full min-w-0 flex-col overflow-hidden rounded-md border border-base-300 bg-base-100 shadow-lg dark:border-gray-700 dark:bg-gray-800"
          :style="dropdownPanelStyle"
        >
        <div class="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
          <div
            v-for="opt in options"
            :key="opt.id || opt"
            class="card-as-row mx-1 rounded-md cursor-pointer transition-all duration-150 ease-in-out"
            :class="{ 'bg-primary text-primary-content': (opt.id || opt) === modelValue, 'hover:bg-base-200 dark:hover:bg-gray-700': (opt.id || opt) !== modelValue }"
            @click="selectOptionAndClose(opt.id || opt)"
          >
            <div class="p-3">
              <div class="flex items-start space-x-3">
                <div class="flex-grow overflow-hidden relative w-full" :class="isRtl === true ? 'text-right' : 'text-left'">
                  <h3 class="text-sm font-semibold leading-tight" :class="[{ 'text-primary-content': (opt.id || opt) === modelValue }, isRtl === true ? 'text-right' : 'text-left']" :style="isRtl === true ? 'padding-right:2%;' : ''">{{ opt.label || opt }}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import { useBodyAnchoredDropdown } from '../composables/useBodyAnchoredDropdown.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Select an Option' },
  options: { type: Array, default: () => [] }
});
const emit = defineEmits(['update:modelValue']);

const { currentLanguage } = useI18n();
const isRtl = computed(() => (['he', 'ar'].includes(currentLanguage.value) === true) ? true : false);

const isOpen = ref(false);
const rootRef = ref(null);
const dropdownListRef = ref(null);
const { panelStyle: dropdownPanelStyle } = useBodyAnchoredDropdown(rootRef, isOpen, { capHeight: 240, zIndex: 265 });

const selectedOptionObj = computed(() => {
  const found = props.options.find(opt => (opt.id || opt) === props.modelValue);
  return found !== undefined ? found : null;
});

const toggleDropdown = () => { isOpen.value = isOpen.value !== true; };
const selectOptionAndClose = (optValue) => {
  if (props.modelValue !== optValue) {
    emit('update:modelValue', optValue);
  }
  isOpen.value = false;
};

const handleClickOutside = (event) => {
  if (isOpen.value !== true) {
    return;
  }
  if (rootRef.value !== null && rootRef.value.contains(event.target) === true) {
    return;
  }
  if (dropdownListRef.value !== null && dropdownListRef.value.contains(event.target) === true) {
    return;
  }
  isOpen.value = false;
};

onMounted(() => { document.addEventListener('click', handleClickOutside, true); });
onBeforeUnmount(() => { document.removeEventListener('click', handleClickOutside, true); });
</script>

<style scoped>
.card-as-row {}
</style>